import React, { useState } from "react";
import {
  Shield,
  Database,
  Network,
  Terminal,
  TrendingUp,
  ArrowUpRight,
  Share2,
  Github,
  ExternalLink,
} from "lucide-react";
import { projects } from "../data/mock";

const iconMap = {
  Shield: Shield,
  Database: Database,
  Network: Network,
  Terminal: Terminal,
  Share2: Share2,
};

const Projects = () => {
  const [hoveredId, setHoveredId] = useState(null);

  return (
    <section id="projects" className="relative py-24 sm:py-32">
      {/* Top divider */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

      <div className="max-w-6xl mx-auto px-6">
        {/* Section header */}
        <div className="mb-16">
          <p className="text-blue-400 text-sm font-medium font-mono tracking-wide mb-3">
            03 — Projects
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
            Featured Projects
          </h2>
          <p className="text-zinc-500 text-base max-w-lg">
            Key infrastructure and automation projects delivering measurable business impact.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 gap-5">
          {projects.map((project, index) => {
            const IconComponent = iconMap[project.icon] || Shield;
            const isHovered = hoveredId === project.id;

            return (
              <div
                key={project.id}
                onMouseEnter={() => setHoveredId(project.id)}
                onMouseLeave={() => setHoveredId(null)}
                className="group relative flex flex-col p-6 sm:p-8 rounded-xl bg-white/[0.02] border border-white/5 hover:border-blue-500/20 transition-all duration-300 hover:bg-blue-500/[0.02] cursor-default"
              >
                {/* Header row */}
                <div className="flex items-start justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/8 border border-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/12 transition-colors duration-300">
                      <IconComponent size={18} className="text-blue-400" />
                    </div>
                    <span className="text-[10px] font-mono text-zinc-700">
                      PROJECT 0{index + 1}
                    </span>
                  </div>
                  <ArrowUpRight
                    size={16}
                    className={`text-zinc-700 transition-all duration-300 ${
                      isHovered
                        ? "text-blue-400 translate-x-0.5 -translate-y-0.5"
                        : ""
                    }`}
                  />
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold text-white mb-3 group-hover:text-blue-50 transition-colors duration-200">
                  {project.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-zinc-500 leading-relaxed mb-5 flex-grow">
                  {project.description}
                </p>

                {/* Impact */}
                <div className="flex items-start gap-2.5 p-3.5 rounded-lg bg-emerald-500/[0.04] border border-emerald-500/10 mb-5">
                  <TrendingUp
                    size={15}
                    className="text-emerald-400 mt-0.5 flex-shrink-0"
                  />
                  <p className="text-xs font-medium text-emerald-400/90 leading-relaxed">
                    {project.impact}
                  </p>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5">
                  {project.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-block px-2.5 py-1 text-[11px] font-medium text-zinc-500 bg-white/[0.03] border border-white/5 rounded-md"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Links (only for projects with live/github URLs) */}
                {(project.liveUrl || project.githubUrl) && (
                  <div className="flex flex-wrap items-center gap-2.5 mt-5 pt-5 border-t border-white/5">
                    {project.liveUrl && (
                      <a
                        href={project.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors duration-200"
                      >
                        <ExternalLink size={13} />
                        Live App
                      </a>
                    )}
                    {project.altUrl && (
                      <a
                        href={project.altUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium text-blue-400 bg-blue-500/[0.06] border border-blue-500/15 hover:bg-blue-500/10 rounded-lg transition-colors duration-200"
                      >
                        <ExternalLink size={13} />
                        {project.altUrl.replace(/^https?:\/\//, "")}
                      </a>
                    )}
                    {project.githubUrl && (
                      <a
                        href={project.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium text-zinc-400 bg-white/[0.03] border border-white/10 hover:border-white/20 hover:text-white rounded-lg transition-all duration-200"
                      >
                        <Github size={13} />
                        GitHub
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Projects;
